/**
 * API client for device-service-staging (OCPP devices: WattelGo, WattelWall)
 * Controls charging via start_transaction / stop_transaction commands
 */

const DEVICE_SERVICE_URL = import.meta.env.VITE_DEVICE_SERVICE_URL || '';

function getAuthToken(): string | null {
  return localStorage.getItem('dashboard_zce_token');
}

function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface DeviceCommandResult {
  success: boolean;
  data?: {
    commandId: string;
    command: string;
    deviceId: string;
    executedAt: string;
    result: { success: boolean; status?: string; transactionId?: number; provisional?: boolean };
    status: string;
    transactionId?: number;
  };
  error?: string;
}

/**
 * Send start_transaction (= ON) to a WattelGo/WattelWall via OCPP
 */
export async function startTransaction(deviceSn: string, connectorId = 1, idTag = 'DASHBOARD'): Promise<DeviceCommandResult> {
  const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/status/chargers/${encodeURIComponent(deviceSn)}/commands`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ command: 'start_transaction', connectorId, idTag }),
  });
  return res.json();
}

/**
 * Send stop_transaction (= OFF) to a WattelGo/WattelWall via OCPP
 */
export async function stopTransaction(deviceSn: string, transactionId: number): Promise<DeviceCommandResult> {
  const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/status/chargers/${encodeURIComponent(deviceSn)}/commands`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ command: 'stop_transaction', transactionId }),
  });
  return res.json();
}

/**
 * Get active transactions for a device (to know the transactionId for stop)
 */
export async function getDeviceStatus(deviceSn: string): Promise<{ success: boolean; data?: any; error?: string }> {
  const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/status/chargers/${encodeURIComponent(deviceSn)}/status`, {
    headers: getHeaders(),
  });
  if (!res.ok) {
    // Fallback: try to get from devices list
    return { success: false, error: `Status endpoint returned ${res.status}` };
  }
  return res.json();
}

/**
 * Get all devices from device-service
 */
export async function getDevices(): Promise<{ success: boolean; data?: any[] }> {
  const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/devices`, {
    headers: getHeaders(),
  });
  return res.json();
}

// ─── Dashboard-service-staging endpoints ────────────────────────────────

const DASHBOARD_API_URL = import.meta.env.VITE_WATTELZCE_API_URL || '';

export interface ChargingSessionStatus {
  deviceSn: string;
  transactionId: number;
  connectorId: number;
  status: string;
  startTime: string;
  durationSeconds: number;
  currentPowerW: number;
  currentEnergyWh: number;
  currentEnergyKwh: number;
  isActive: boolean;
}

/**
 * Get live OCPP session status for charging bornes (energy, power, duration).
 * Polls the backend which reads from charging_transactions + Redis OCPP cache.
 * Similar to Flutter's FetchOcppSessionStatus polling every 10s.
 */
export async function getChargingSessionStatus(deviceSns: string[]): Promise<{ success: boolean; data?: ChargingSessionStatus[] }> {
  if (deviceSns.length === 0) return { success: true, data: [] };
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${DASHBOARD_API_URL}/api/devices/charging/session-status?deviceSns=${encodeURIComponent(deviceSns.join(','))}`, {
    headers,
  });
  return res.json();
}
